/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
import { computed, observable, action } from 'mobx';
import { VocabularyItem, VocabularyNamespaces } from 'types';
import { TFunction } from 'i18next';
import { removeSessionData } from '../services/StorageService';
import type { IRootStore } from './IRootStore';

class UIStore {
  tFunction: TFunction = (s: string) => s;

  @observable currentLanguage = 'en-US';

  @observable languages: VocabularyItem[] = [];

  @observable theme = 'default';

  @observable isTourDisplayed = false;

  @observable videoToPlay: string = '';

  rootStore: IRootStore;

  @observable currentCourseLanguages: string[] | null = null;

  /**
   * Reason ids that request the Intercom widget to be hidden (e.g. 'course:ai-assistant').
   * Must be observable.array() so MobX tracks push/splice; plain [] stays a normal Array and won't trigger reactions.
   */
  @observable intercomHideReasons = observable.array<string>();

  /**
   * Reason ids that request the Pendo launcher to be hidden (e.g. 'course:ai-assistant').
   */
  @observable pendoHideReasons = observable.array<string>();

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action toggleTour = (video?: string) => {
    this.videoToPlay = video;
    this.isTourDisplayed = !this.isTourDisplayed;
  };

  @computed get isChineseInstance() {
    const { configStore } = this.rootStore;
    return configStore.rezodePlatform === 'china';
  }

  onLanguageChange = async (language = 'en-US') => {
    const { vocabularyStore } = this.rootStore;

    this.currentLanguage = language;

    try {
      await vocabularyStore.getVocabularyList(language);

      const languages = await vocabularyStore.getVocabularyByNamespace(
        VocabularyNamespaces.Languages,
        language,
      );

      this.languages = languages || this.languages;
      return this.languages.find((lang) => lang.token === language);
    } catch (e) {
      console.error(e);
    }
    return this.currentLanguage;
  };

  clearLabAutostartSettings = () => {
    removeSessionData('showLabAutostart');
  };

  /**
   * Stores which languages are supported by the self-paced course that is currently active
   * Used to highlight certain languages in the global UI when a course is open
   */
  @action reportActiveCourseTranslations(courseLanguages?: string[]) {
    if (courseLanguages) {
      this.currentCourseLanguages = courseLanguages;
    } else {
      this.currentCourseLanguages = null;
    }
  }

  /**
   * True when any component has registered a reason to hide Intercom.
   */
  @computed get isIntercomHidden(): boolean {
    return this.intercomHideReasons.length > 0;
  }

  /**
   * Register a reason to hide the Intercom widget (e.g. 'course:ai-assistant' when AI sidebar is open).
   */
  @action registerIntercomHideReason(id: string): void {
    if (!this.intercomHideReasons.includes(id)) {
      this.intercomHideReasons.push(id);
    }
  }

  /**
   * Unregister a reason; Intercom is shown again when no reasons remain.
   */
  @action unregisterIntercomHideReason(id: string): void {
    const idx = this.intercomHideReasons.indexOf(id);
    if (idx >= 0) {
      this.intercomHideReasons.splice(idx, 1);
    }
  }

  /**
   * True when any component has registered a reason to hide the Pendo launcher.
   */
  @computed get isPendoHidden(): boolean {
    return this.pendoHideReasons.length > 0;
  }

  /**
   * Register a reason to hide the Pendo launcher (e.g. 'course:ai-assistant' when AI sidebar is open).
   */
  @action registerPendoHideReason(id: string): void {
    if (!this.pendoHideReasons.includes(id)) {
      this.pendoHideReasons.push(id);
    }
  }

  /**
   * Unregister a reason; Pendo launcher is shown again when no reasons remain.
   */
  @action unregisterPendoHideReason(id: string): void {
    const idx = this.pendoHideReasons.indexOf(id);
    if (idx >= 0) {
      this.pendoHideReasons.splice(idx, 1);
    }
  }
}

export default UIStore;
